import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { X } from 'lucide-react';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { FieldLabel } from '../../../../components/ui/FieldLabel';
import { Skeleton } from '../../../../components/ui/Skeleton';

const fmtBucket = (v) => (v || '').slice(0, 10);

const TimelineTab = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const [granularity, setGranularity] = useState('day');
  const [delivererId, setDelivererId] = useState('');
  // Nombre legible del repartidor seleccionado, para etiquetar la Line del overlay
  // con el nombre real en vez del ObjectId truncado.
  const [delivererLabel, setDelivererLabel] = useState('');
  const [delivererSearch, setDelivererSearch] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [global, setGlobal] = useState([]);
  const [overlay, setOverlay] = useState([]);
  const blurTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setDriversLoading(true);
    deliveryQualityService
      .getByDriver({ ...filters, limit: 100, min_count: 5, sort_by: 'avgScore', sort_dir: 1 })
      .then((r) => alive && setDrivers(r.data?.data || []))
      .catch(() => alive && setDrivers([]))
      .finally(() => alive && setDriversLoading(false));
    return () => {
      alive = false;
    };
  }, [filters]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const calls = [deliveryQualityService.getTimeline({ ...filters, granularity })];
    if (delivererId.trim()) {
      calls.push(
        deliveryQualityService.getTimeline({
          ...filters,
          granularity,
          deliverer_id: delivererId.trim(),
        }),
      );
    }
    Promise.all(calls)
      .then((res) => {
        if (!alive) return;
        setGlobal(res[0].data?.data || []);
        setOverlay(res[1]?.data?.data || []);
      })
      .catch((e) => alive && setError(e?.message || 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filters, granularity, delivererId]);

  useEffect(
    () => () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    },
    [],
  );

  const filteredDrivers = useMemo(() => {
    const q = delivererSearch.trim().toLowerCase();
    if (!q) return drivers.slice(0, 30);
    return drivers
      .filter((r) => {
        const haystack = [
          r.key,
          r.user?.fullName,
          r.user?.name,
          r.user?.lastName,
          r.user?.email,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 30);
  }, [drivers, delivererSearch]);

  const driverLabel = (d) =>
    d?.user?.fullName ||
    [d?.user?.name, d?.user?.lastName].filter(Boolean).join(' ') ||
    d?.user?.email ||
    d?.key ||
    '';

  const selectDriver = (driver) => {
    setDelivererId(driver.key || '');
    setDelivererLabel(driverLabel(driver));
    setDelivererSearch(driverLabel(driver));
    setShowDropdown(false);
  };

  const clearDriver = () => {
    setDelivererId('');
    setDelivererLabel('');
    setDelivererSearch('');
    setShowDropdown(false);
  };

  const handleDriverInputChange = (e) => {
    const v = e.target.value;
    setDelivererSearch(v);
    setShowDropdown(true);
    if (!v.trim() && delivererId) {
      setDelivererId('');
      setDelivererLabel('');
    }
  };

  // Mezcla por bucket para mostrar overlay del driver junto al global
  const merged = (() => {
    const map = new Map();
    global.forEach((p) => map.set(p.bucket, { bucket: fmtBucket(p.bucket), global: p.avg_score }));
    overlay.forEach((p) => {
      const key = p.bucket;
      const prev = map.get(key) || { bucket: fmtBucket(key) };
      prev.driver = p.avg_score;
      map.set(key, prev);
    });
    return Array.from(map.values()).sort((a, b) => a.bucket.localeCompare(b.bucket));
  })();

  return (
    <>
      <Card padding="md" className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-3">
            <FieldLabel>{t('Granularidad')}</FieldLabel>
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}
              className="w-full h-10 rounded-lg border border-border bg-bg px-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="day">{t('Día')}</option>
              <option value="week">{t('Semana')}</option>
              <option value="month">{t('Mes')}</option>
            </select>
          </div>
          <div className="md:col-span-6 relative">
            <FieldLabel>{t('Overlay por repartidor')}</FieldLabel>
            <div className="relative">
              <Input
                type="text"
                placeholder={
                  driversLoading
                    ? t('Cargando repartidores…')
                    : t('Buscar por nombre, correo o ID…')
                }
                value={delivererSearch}
                onChange={handleDriverInputChange}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => {
                  blurTimerRef.current = setTimeout(() => setShowDropdown(false), 150);
                }}
                className={delivererId || delivererSearch ? 'pr-9' : undefined}
              />
              {(delivererId || delivererSearch) && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-fg-subtle hover:text-fg hover:bg-bg-muted"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearDriver}
                  title={t('Limpiar')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {showDropdown && filteredDrivers.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-20 mt-0.5 max-h-[280px] overflow-y-auto rounded-lg border border-border bg-bg shadow-lg">
                {filteredDrivers.map((d) => {
                  const fullName = driverLabel(d) || t('Sin nombre');
                  const isSelected = d.key === delivererId;
                  return (
                    <div
                      key={d.key || `row-${fullName}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectDriver(d);
                      }}
                      className={`px-3 py-2 cursor-pointer border-b border-border last:border-b-0 ${
                        isSelected ? 'bg-bg-muted' : 'hover:bg-bg-muted'
                      }`}
                    >
                      <div className="font-semibold text-fg text-[13px]">{fullName}</div>
                      {d.user?.email && (
                        <div className="text-[11px] text-fg-muted">{d.user.email}</div>
                      )}
                      <div className="text-[11px] text-fg-subtle flex justify-between gap-2">
                        <code>{d.key || 'sin id'}</code>
                        <span>
                          score {(d.avg_score ?? 0).toFixed(1)} · {d.total_evaluations} {t('entregas')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {showDropdown &&
              !driversLoading &&
              filteredDrivers.length === 0 &&
              delivererSearch.trim() && (
                <div className="absolute top-full left-0 right-0 z-20 mt-0.5 rounded-lg border border-border bg-bg px-3 py-2.5 text-xs text-fg-muted shadow-lg">
                  {t('Sin coincidencias.')}
                </div>
              )}
          </div>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-bg-muted text-sm font-semibold text-fg">
          {t('Score promedio en el tiempo')}
        </div>
        <div className="p-4" style={{ height: 380 }}>
          {loading ? (
            <Skeleton className="w-full h-full rounded-lg" />
          ) : error ? (
            <div className="rounded-lg border border-status-error-soft bg-status-error-soft text-status-error px-4 py-3 text-sm">
              {error}
            </div>
          ) : !merged.length ? (
            <div className="rounded-lg border border-border bg-bg-muted text-fg-muted px-4 py-3 text-sm">
              {t('Sin datos para el rango.')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="global" name={t('Global')} stroke="var(--text-secondary)" strokeWidth={2} dot={false} />
                {delivererId.trim() && (
                  <Line
                    type="monotone"
                    dataKey="driver"
                    name={delivererLabel || `Driver ${delivererId.trim().slice(0, 8)}…`}
                    stroke="var(--color-primary)"
                    strokeWidth={2}
                    dot={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </>
  );
};

export default TimelineTab;
